import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import axios from "axios";
import { FormEvent, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
    EntryField,
    EntryTitle,
    EntryWrapper,
    FormWrapper,
} from "../../../components/customStyles/form";
import Progress from "../../../components/progress/Progress";
import { ERole } from "../../../models/enums/role";
import { DEFAULT_ROUNDING_SETTINGS, IPropertyGroup, RoundingDirection } from "../../../models/propertyGroup";
import IUser from "../../../models/user";
import propertyGroupService from "../../../services/propertyGroupService/PropertyGroupService";
import UserService from "../../../services/user/UserService";
import buildingService from './../../../services/building/BuildingService';
import { IBuilding, IDuplicateCode } from "./PropertyGroupAddPage";
import { theme } from "../../../styles/themes/mainTheme";
import CloseIcon from '@mui/icons-material/Close';
import EditOnly from "../../../components/permissions/EditOnly";

const SUCCESS_STATUS_CODE = 200;
export const HOURS_WORKED_MIN_VALUE = 1;
export const HOURS_WORKED_MAX_VALUE = 24;
export const BREAK_MINUTES_MIN_VALUE = 1;
export const BREAK_MINUTES_MAX_VALUE = 240;


const PropertyGroupEditPage = () => {
    const { t } = useTranslation(["common", "admin"]);
    const navigate = useNavigate();
    const location = useLocation();
    const propertyGroup = location.state as IPropertyGroup;
    const [name, setName] = useState(propertyGroup.name);
    const [isSelfieRequired, setIsSelfieRequired] = useState(propertyGroup.isSelfieRequired);
    const [punchDeviceId, setPunchDeviceId] = useState(propertyGroup.punchDeviceId);
    const [acombaProjectId, setAcombaProjectId] = useState(propertyGroup.acombaProjectId);
    const [scanInactivityTimeout, setScanInactivityTimeout] = useState(propertyGroup.scanInactivityTimeout);
    const [codes, setCodes] = useState<string[]>(propertyGroup.codes);
    const [selectedBuildingsIds, setSelectedBuildingsIds] = useState<number[]>(propertyGroup.buildings.map(b => b.id));
    const [codeInput, setCodeInput] = useState("");
    const [isErrorMessageVisible, setIsErrorMessageVisible] = useState(false);
    const [isPropertyChanged, setIsPropertyChanged] = useState(false);
    const [availableBuildings, setAvailableBuildings] = useState<IBuilding[]>([]);
    const [isFormSending, setIsFormSending] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [errorCodes, setErrorCodes] = useState<string | null>(null);
    const [buildingInput, setBuildingInput] = useState("");
    const [userInput, setUserInput] = useState("");
    const [selectedBuildings, setSelectedBuildings] = useState<IBuilding[]>([]);
    const [availableUsers, setAvailableUsers] = useState<IUser[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<IUser[]>([]);
    const [isBreakDeductionEnabled, setIsBreakDeductionEnabled] = useState(propertyGroup.isBreakDeductionEnabled);
    const [breakRules, setBreakRules] = useState(() =>
        (propertyGroup.employeeBreakRules || []).sort((a, b) => a.minHoursWorked - b.minHoursWorked).map(rule => ({
            id: rule.id,
            propertyGroupId: rule.propertyGroupId,
            minHoursWorked: rule.minHoursWorked,
            totalBreakMinutes: rule.totalBreakMinutes,
        }))
    );
    const [timeRoundingSettings, setTimeRoundingSettings] = useState(propertyGroup.timeRoundingSettings);

    useEffect(() => {
        if (propertyGroup?.buildings) {
            const transformedBuildings = propertyGroup.buildings.map((building) => ({
                id: building.id,
                name: building.name,
                isAlreadyAssigned: false,
            }));
            setSelectedBuildings(transformedBuildings);
        }
    }, [propertyGroup]);

    const hasAssociatedBuildings = propertyGroup.buildings.length > 0;

    useEffect(() => {
        buildingService.GetBuildingsDetails().then((fetchedBuildings) => {
            propertyGroupService.GetPropertyGroups().then((propertyGroups) => {
                const buildingsWithAssignment = fetchedBuildings.map((building) => {
                    const isAssigned = propertyGroups.some((group) =>
                        group.buildings.map(b => b.id).includes(building.id)
                    );

                    return {
                        ...building,
                        isAlreadyAssigned: isAssigned && !selectedBuildingsIds.includes(building.id),
                    };
                });
                setAvailableBuildings(buildingsWithAssignment);
            });
        })

        UserService.GetUsersByCompany().then((fetchedUsers) => {
            setAvailableUsers(fetchedUsers);
            setSelectedUsers(propertyGroup.users);
        });

    }, []);

    const onUpdateProperty = async (event: FormEvent) => {
        event.preventDefault();
        setIsFormSending(true);

        try {
            const response = await propertyGroupService.PutPropertyGroup({
                id: propertyGroup.id,
                name,
                buildingsIds: selectedBuildingsIds,
                codes,
                scanInactivityTimeout,
                isSelfieRequired,
                punchDeviceId,
                userIds: selectedUsers.map(user => user.id),
                acombaProjectId: acombaProjectId || undefined,
                isBreakDeductionEnabled,
                employeeBreakRules: breakRules,
                timeRoundingSettings: timeRoundingSettings,
            });
            if (response.status === SUCCESS_STATUS_CODE) {
                navigate("..");
            }

        } catch (error) {
            if (!axios.isAxiosError(error)) return;
            const errorResponse = error.response?.data.message as IDuplicateCode[];
            if (errorResponse) setErrorCodes(`${errorResponse.map(item => item.message).join('\n')} `);
        } finally {
            setIsFormSending(false);
        }

    };

    useEffect(() => {
        const isNameChanged = name !== propertyGroup.name;
        const isSelfieRequiredChanged = isSelfieRequired !== propertyGroup.isSelfieRequired;
        const isTimeOutChanged = scanInactivityTimeout !== propertyGroup.scanInactivityTimeout;
        const areBuildingsChanged = selectedBuildingsIds.sort().toString() !== propertyGroup.buildings.map(b => b.id).sort().toString();
        const areCodesChanged = codes.sort().toString() !== propertyGroup.codes.sort().toString();
        const isPunchDeviceIdChanged = punchDeviceId !== propertyGroup.punchDeviceId;
        const areUsersChanged = selectedUsers.map(user => user.id).sort().toString() !== propertyGroup.users.map(user => user.id).sort().toString();
        const isAcombaProjectIdChanged = acombaProjectId !== propertyGroup.acombaProjectId;
        const isBreakDeductionEnabledChanged = isBreakDeductionEnabled !== propertyGroup.isBreakDeductionEnabled;
        const isBreakRulesChanged = JSON.stringify(breakRules) !== JSON.stringify(propertyGroup.employeeBreakRules);
        const isTimeRoundingSettingsChanged = JSON.stringify(timeRoundingSettings) !== JSON.stringify(propertyGroup.timeRoundingSettings);

        setIsPropertyChanged(isNameChanged || areBuildingsChanged || areCodesChanged || isTimeOutChanged || isSelfieRequiredChanged || isPunchDeviceIdChanged || areUsersChanged || isAcombaProjectIdChanged || isBreakDeductionEnabledChanged || isBreakRulesChanged || isTimeRoundingSettingsChanged);
    }, [propertyGroup, name, selectedBuildingsIds, codes, scanInactivityTimeout, isSelfieRequired, punchDeviceId, selectedUsers, acombaProjectId, isBreakDeductionEnabled, breakRules, timeRoundingSettings]);

    const onAddCode = (value: string) => {
        const trimmedValue = value.trim();
        if (!trimmedValue || codes.includes(trimmedValue)) return;

        setCodes([...codes, trimmedValue]);
        setCodeInput("");
    };

    const openConfirmDialog = () => setIsConfirmDialogOpen(true);
    const closeConfirmDialog = () => setIsConfirmDialogOpen(false);


    const onRemovePropertyGroup = () => {
        if (hasAssociatedBuildings) {
            setIsErrorMessageVisible(true);
            return;
        }

        setIsFormSending(true);
        propertyGroupService.DeletePropertyGroup(propertyGroup.id)
            .then(() => navigate(".."))
            .finally(() => setIsFormSending(false));
    };

    const isFormValid = () => {
        if (!name.trim()) return false;
        if (!scanInactivityTimeout || scanInactivityTimeout < 1) return false;
        if (isBreakDeductionEnabled && breakRules.length === 0) return false;
        if (breakRules.length && breakRules.some(rule => rule.minHoursWorked < HOURS_WORKED_MIN_VALUE || rule.minHoursWorked > HOURS_WORKED_MAX_VALUE || rule.totalBreakMinutes < BREAK_MINUTES_MIN_VALUE || rule.totalBreakMinutes > BREAK_MINUTES_MAX_VALUE)) return false;
        const uniqueBreakRules = new Set(breakRules.map(rule => rule.minHoursWorked));
        if (uniqueBreakRules.size !== breakRules.length) return false;

        return true;
    };

    const addBreakRule = () => {
        const usedValues = breakRules.map(r => r.minHoursWorked);
        let newMin = HOURS_WORKED_MIN_VALUE;
        while (usedValues.includes(newMin) && newMin <= HOURS_WORKED_MAX_VALUE) newMin++;

        setBreakRules([
            ...breakRules,
            {
                id: 0,
                propertyGroupId: propertyGroup.id,
                minHoursWorked: newMin,
                totalBreakMinutes: 15
            }
        ]);
    }

    return (
        <>
            <Box component={Paper} sx={{ width: "100%" }}>
                <form autoComplete="off" onSubmit={onUpdateProperty}>
                    <FormWrapper>
                        {errorCodes && errorCodes.split('\n').map((line, index) => (
                            <Typography marginTop={2} key={index} variant="body2" color="error" >{line}</Typography>
                        ))
                        }
                        <EntryWrapper>
                            <EntryTitle>
                                {t("table.property.name")} *
                            </EntryTitle>
                            <EntryField>
                                <TextField
                                    required={true}
                                    style={{ textAlign: "left", width: "100%" }}
                                    value={name}
                                    onChange={(e) => {
                                        setName(e.target.value);
                                    }}
                                    slotProps={{ htmlInput: { maxLength: 50 } }}
                                />
                            </EntryField>
                        </EntryWrapper>

                        <EntryWrapper>
                            <EntryTitle>{t("table.building.buildings")}</EntryTitle>
                            <EntryField>
                                <Autocomplete
                                    fullWidth
                                    multiple
                                    freeSolo
                                    value={selectedBuildings.map((building) => building.name)}
                                    onChange={(_, newBuildingNames) => {
                                        const newSelectedBuildings = newBuildingNames.map((name) =>
                                            availableBuildings.find((building) => building.name === name)
                                        ).filter(Boolean) as IBuilding[];

                                        setSelectedBuildings(newSelectedBuildings);
                                        setSelectedBuildingsIds(newSelectedBuildings.map((b) => b.id));
                                    }}
                                    inputValue={buildingInput}
                                    onInputChange={(_, value) => setBuildingInput(value)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" && buildingInput.trim()) {
                                            event.preventDefault();
                                            const matchedBuilding = availableBuildings.find(
                                                (building) => building.name.toLowerCase() === buildingInput.trim().toLowerCase()
                                            );

                                            if (matchedBuilding && !selectedBuildingsIds.includes(matchedBuilding.id)) {
                                                setSelectedBuildings([...selectedBuildings, matchedBuilding]);
                                                setSelectedBuildingsIds([...selectedBuildingsIds, matchedBuilding.id]);
                                            }
                                            setBuildingInput("");
                                        }
                                    }}
                                    options={availableBuildings.map((building) => building.name)}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                label={option}
                                                {...getTagProps({ index })}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                        />
                                    )}
                                    disabled={true}
                                />
                            </EntryField>
                        </EntryWrapper>

                        <EntryWrapper>
                            <EntryTitle>{t("table.user.users")}</EntryTitle>
                            <EntryField>
                                <Autocomplete
                                    fullWidth
                                    multiple
                                    freeSolo={false}
                                    options={availableUsers}
                                    value={selectedUsers}
                                    getOptionLabel={(option) => option.name}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    onChange={(_, newSelectedUsers) => {
                                        setSelectedUsers(newSelectedUsers);
                                    }}
                                    inputValue={userInput}
                                    onInputChange={(_, value) => setUserInput(value)}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                }}
                                            >
                                                <span>{option.name}</span>
                                                {option.role !== ERole.Employee && (
                                                    <Chip label={t("table.user.Manager")} color="primary" size="small" />
                                                )}
                                            </div>
                                        </li>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                label={
                                                    <span>
                                                        {option.name}
                                                        {option.role !== ERole.Employee && (
                                                            <span style={{ marginLeft: 6, fontSize: 10, background: '#1976d2', color: 'white', borderRadius: 100, padding: '2px 6px' }}>
                                                                {t("table.user.Manager").charAt(0).toUpperCase()}
                                                            </span>
                                                        )}
                                                    </span>
                                                }
                                                {...getTagProps({ index })}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                        />
                                    )}
                                />

                            </EntryField>
                        </EntryWrapper>

                        <EntryWrapper>
                            <EntryTitle>Codes </EntryTitle>
                            <Box sx={{
                                display: "flex",
                                flex: 1,
                                flexDirection: "column",
                                justifyContent: "flex-end",
                                alignItems: "left",
                                marginTop: "1em",
                            }}>
                                <Autocomplete
                                    fullWidth
                                    multiple
                                    freeSolo
                                    value={codes}
                                    onChange={(_, newCodes) => setCodes(newCodes)}
                                    inputValue={codeInput}
                                    onInputChange={(_, value) => {
                                        setCodeInput(value);
                                    }}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            onAddCode(codeInput);
                                        }
                                    }}
                                    options={[]}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                label={option}
                                                {...getTagProps({ index })}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            onBlur={(event) => {
                                                onAddCode(event.target.value);
                                            }}
                                            InputLabelProps={{
                                                children: null,
                                            }}
                                        />
                                    )}
                                />

                            </Box>
                        </EntryWrapper>

                        <EntryWrapper>
                            <EntryTitle>
                                {t("table.property.scanInactivity")} *
                            </EntryTitle>
                            <EntryField>
                                <TextField
                                    type="number"
                                    required={true}
                                    style={{ textAlign: "left", width: "100%" }}
                                    value={scanInactivityTimeout}
                                    onChange={(e) => {
                                        setScanInactivityTimeout(Number(e.target.value));
                                    }}
                                    slotProps={{ htmlInput: { maxLength: 50, min: 1 } }}

                                />
                            </EntryField>
                        </EntryWrapper>

                        <EntryWrapper>
                            <EntryTitle>{t("table.property.isSelfieRequired")} </EntryTitle>
                            <EntryField>
                                <EntryField>
                                    <Checkbox
                                        defaultChecked={false}
                                        checked={isSelfieRequired}
                                        onChange={(e) => {
                                            setIsSelfieRequired(!isSelfieRequired);
                                        }}
                                    />
                                </EntryField>
                            </EntryField>
                        </EntryWrapper>

                        <EntryWrapper>
                            <EntryTitle>
                                {t("table.property.punchDeviceId")}
                            </EntryTitle>
                            <EntryField>
                                <TextField
                                    style={{ textAlign: "left", width: "100%" }}
                                    value={punchDeviceId}
                                    onChange={(e) => {
                                        setPunchDeviceId(e.target.value);
                                    }}
                                    slotProps={{ htmlInput: { maxLength: 45 } }}
                                />
                            </EntryField>
                        </EntryWrapper>


                        <EntryWrapper>
                            <EntryTitle>
                                {t("table.property.acombaProjectId")}
                            </EntryTitle>
                            <EntryField>
                                <TextField
                                    type="number"
                                    style={{ textAlign: "left", width: "100%" }}
                                    value={acombaProjectId || undefined}
                                    onChange={(e) => {
                                        setAcombaProjectId(Number(e.target.value) || undefined);
                                    }}
                                    slotProps={{ htmlInput: { min: 1 } }}
                                />
                            </EntryField>
                        </EntryWrapper>

                        {/* <EntryWrapper>
                            <EntryTitle>{t("table.property.breakDeduction")}</EntryTitle>
                            <EntryField>
                                <Checkbox
                                    checked={isBreakDeductionEnabled}
                                    onChange={() => setIsBreakDeductionEnabled(!isBreakDeductionEnabled)}
                                />
                            </EntryField>
                        </EntryWrapper>

                        {isBreakDeductionEnabled && (
                            <Box sx={{ borderLeft: '3px solid #1976d2', paddingLeft: 2 }}>
                                <Typography variant="body1" fontWeight="bold">{t("table.property.breakRules")}</Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 2 }}>
                                    <Trans i18nKey="table.property.breakRulesCaption" components={{ 1: <strong />, 3: <strong /> }} />
                                </Typography>

                                {breakRules?.map((rule, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
                                        <Typography variant="body2">{t("table.property.ruleText1")}</Typography>
                                        <TextField
                                            label={t("globalDashboard.hours")}
                                            type="number"
                                            value={rule.minHoursWorked}
                                            slotProps={{ htmlInput: { min: HOURS_WORKED_MIN_VALUE, max: HOURS_WORKED_MAX_VALUE } }}
                                            error={breakRules.some((r, i) => i !== index && r.minHoursWorked === rule.minHoursWorked) || rule.minHoursWorked < HOURS_WORKED_MIN_VALUE || rule.minHoursWorked > HOURS_WORKED_MAX_VALUE}
                                            onChange={(e) => {
                                                const updated = [...breakRules];
                                                updated[index].minHoursWorked = parseFloat(e.target.value);
                                                setBreakRules(updated);
                                            }}
                                        />
                                        <Typography variant="body2">{t("table.property.ruleText2")}</Typography>
                                        <TextField
                                            label={t("globalDashboard.minutes")}
                                            type="number"
                                            value={rule.totalBreakMinutes}
                                            slotProps={{ htmlInput: { min: BREAK_MINUTES_MIN_VALUE, max: BREAK_MINUTES_MAX_VALUE } }}
                                            error={rule.totalBreakMinutes < BREAK_MINUTES_MIN_VALUE || rule.totalBreakMinutes > BREAK_MINUTES_MAX_VALUE}
                                            onChange={(e) => {
                                                const updated = [...breakRules];
                                                updated[index].totalBreakMinutes = parseInt(e.target.value);
                                                setBreakRules(updated);
                                            }}
                                        />
                                        <Typography variant="body2">{t("table.property.ruleText3")}</Typography>
                                        <IconButton
                                            onClick={() => {
                                                const updated = breakRules.filter((_, i) => i !== index);
                                                setBreakRules(updated);
                                            }}
                                            sx={{ color: theme.palette.error.main }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                ))}

                                <Box sx={{ mt: 1 }}>
                                    <Button
                                        variant="outlined"
                                        disabled={breakRules.length >= HOURS_WORKED_MAX_VALUE}
                                        onClick={() => addBreakRule()}
                                    >
                                        {t("table.property.addRule")}
                                    </Button>
                                </Box>
                            </Box>
                        )} */}

                        {/* <EntryWrapper>
                            <EntryTitle>{t("table.property.rounding")}</EntryTitle>
                            <EntryField>
                                <Checkbox
                                    checked={timeRoundingSettings?.isEnabled || false}
                                    onChange={() =>
                                        setTimeRoundingSettings((prev) =>
                                            prev
                                                ? { ...prev, isEnabled: !prev.isEnabled }
                                                : DEFAULT_ROUNDING_SETTINGS,
                                        )
                                    }
                                />
                            </EntryField>
                        </EntryWrapper>

                        {timeRoundingSettings && timeRoundingSettings.isEnabled && (
                            <Box sx={{ borderLeft: '3px solid #1976d2', pl: 2, mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Typography variant="body1" fontWeight="bold">{t("table.property.roundingRules")}</Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2">{t("table.property.roundingDirection")}</Typography>
                                    <TextField
                                        select
                                        size="small"
                                        value={timeRoundingSettings.direction}
                                        onChange={(e) =>
                                            setTimeRoundingSettings((prev) => (prev && { ...prev, direction: parseInt(e.target.value) as RoundingDirection }))
                                        }
                                    >
                                        <MenuItem value={RoundingDirection.UP}>{t("table.property.roundUp")}</MenuItem>
                                        <MenuItem value={RoundingDirection.DOWN}>{t("table.property.roundDown")}</MenuItem>
                                        <MenuItem value={RoundingDirection.NEAREST}>{t("table.property.roundNearest")}</MenuItem>
                                    </TextField>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2">{t("table.property.roundingInterval")}</Typography>
                                    <TextField
                                        select
                                        size="small"
                                        value={timeRoundingSettings.interval}
                                        onChange={(e) =>
                                            setTimeRoundingSettings((prev) => (prev && { ...prev, interval: parseInt(e.target.value) }))
                                        }
                                    >
                                        {[5, 10, 15, 30].map((min) => (
                                            <MenuItem key={min} value={min}>
                                                {min} {t("globalDashboard.minutes")}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                            </Box>
                        )} */}


                        <EntryWrapper
                            sx={{
                                justifyContent: "flex-end",
                                marginBlock: "1em",
                            }}
                        >

                            <EntryWrapper
                                sx={{ justifyContent: "flex-end" }}
                            >
                                <Progress isLoading={isFormSending}>
                                    <EditOnly>
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={openConfirmDialog}
                                            disabled={isFormSending}
                                            sx={{ marginTop: "4em" }}
                                        >
                                            {t("table.property.removeGroup")}
                                        </Button>
                                    </EditOnly>

                                    <EditOnly>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            disabled={!isPropertyChanged || isFormSending || !isFormValid()}
                                            sx={{ marginTop: "4em", marginLeft: "1em" }}
                                        >
                                            {t("table.property.edit")}
                                        </Button>
                                    </EditOnly>

                                </Progress>

                            </EntryWrapper>

                        </EntryWrapper>
                    </FormWrapper>
                </form>
            </Box >
            <Dialog
                open={isConfirmDialogOpen}
                onClose={closeConfirmDialog}
            >
                <DialogTitle>{t("table.property.propertyGroup")}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {hasAssociatedBuildings
                            ? t("table.property.warningDelete")
                            : t("table.property.confirmDeleteTitle")
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirmDialog} color="primary">
                        {hasAssociatedBuildings ? "OK" : t("table.property.cancel")}
                    </Button>
                    {!hasAssociatedBuildings && (
                        <Button onClick={onRemovePropertyGroup} color="error" autoFocus>
                            {t("table.property.confirm")}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

        </>
    );
};

export default PropertyGroupEditPage;